import { blue, green, orange, red } from '@ant-design/colors';
import { notification } from 'antd';
import type { TotalizadorListData } from '@/components/totalizador/TotalizadorList';
import type { ApiFinanceiroMovimento } from '@/features/financeiro-movimento/types/ApiFinanceiroMovimento';
import type { RootDispatch, RootState } from '@/state/store';
import type { ApiResponse } from '@/std/api/types';
import type { SimNao } from '@/types';
import type { Totalizador } from '@/types/schemas/Totalizador';
import { getEmpresaIdpk } from '@/features/login/util';
import { marcadoresToStringIdpk } from '@/features/marcador/util';
import { type BuildUrlParams, buildUrl } from '@/std/api/buildUrl';
import { comTokenGet, comTokenPost, comTokenPut, comTokenRemove } from '@/std/api/comToken';
import { throwIfResponseIsErr } from '@/std/api/util';
import { endReduxFnError, endReduxFnOk, startReduxFn } from '@/std/redux';
import { TipoConsulta } from '@/types/enum';
import { Endpoint } from '@/types/enum/endpoint';
import { GetTypes } from '@/types/interfaces/GetTypes';
import type {
    ContasPagarFile,
    ContasPagarPostBody,
    ContasPagarPostLancamentoBody,
    ContasPagarPutBody,
} from './types';

const DATE_FORMAT = 'DD-MM-YYYY';

export const effects = (dispatch: RootDispatch) => ({
    async deleteAnexo(
        payload: {
            financeiro_movimento_idpk: number;
            anexo_idpk: number;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasPagar.setState({
            deleteAnexo: startReduxFn(state.contasPagar.deleteAnexo.data),
        });

        try {
            const { financeiro_movimento_idpk, anexo_idpk } = payload;

            const params = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_movimento_idpk,
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoAnexo, params, anexo_idpk);
            const response: ApiResponse = await comTokenRemove(url);
            throwIfResponseIsErr(response);

            dispatch.contasPagar.setState({
                deleteAnexo: endReduxFnOk(null),
            });
        } catch (error) {
            dispatch.contasPagar.setState({
                deleteAnexo: endReduxFnError(error),
            });
        }
    },

    async estornar(payload: { fmo_idpk: number; data_estorno?: string }): Promise<void> {
        dispatch.contasPagar.setState({
            estornar: startReduxFn(),
        });

        try {
            const { fmo_idpk, data_estorno } = payload;
            const params = { data_estorno, empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.FinanceiroMovimentoEstornar, params, fmo_idpk);
            const response: ApiResponse = await comTokenPut(url);
            throwIfResponseIsErr(response);

            await dispatch.contasPagar.get({
                type: GetTypes.editRecord,
            });

            dispatch.contasPagar.setState({
                estornar: endReduxFnOk(null),
            });
        } catch (error) {
            dispatch.contasPagar.setState({
                estornar: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Falha ao estornar conta',
            });
        }
    },

    async estornarLancamento(payload: {
        fml_idpk: number;
        fml_agrupado_id?: string;
        fmo_idpk?: number;
        data_estorno?: string;
    }): Promise<void> {
        dispatch.contasPagar.setState({
            estornarLancamento: startReduxFn(),
        });

        try {
            const { fml_idpk, fml_agrupado_id, fmo_idpk, data_estorno } = payload;

            const params: BuildUrlParams = {
                data_estorno,
                empresa_idpk: getEmpresaIdpk(),
            };

            let url = '';

            if (fml_agrupado_id) {
                params.grupo_id = fml_agrupado_id;
                url = buildUrl(Endpoint.FinanceiroMovimentoLancamentoEstornar, params);
            } else {
                url = buildUrl(Endpoint.FinanceiroMovimentoLancamentoEstornar, params, fml_idpk);
            }

            const response: ApiResponse = await comTokenPut(url);
            throwIfResponseIsErr(response);

            if (fmo_idpk) {
                await dispatch.contasPagar.get({ type: GetTypes.firstGet });
            }

            dispatch.contasPagar.setState({
                estornarLancamento: endReduxFnOk(null),
            });
        } catch (error) {
            dispatch.contasPagar.setState({
                estornarLancamento: endReduxFnError(error),
            });
        }
    },

    async get(
        payload: {
            type: GetTypes;
            customUrl?: string;
            conta_idpk?: number;
            tipo_consulta?: TipoConsulta;
            needTotais?: boolean;
            onlyPagFor?: boolean;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasPagar.setState({
            get: startReduxFn(state.contasPagar.get.data),
        });

        try {
            const {
                type,
                customUrl,
                conta_idpk,
                tipo_consulta = TipoConsulta.Resumida,
                needTotais = true,
                onlyPagFor = false,
            } = payload;

            const { contasPagar } = state;

            const {
                filterStatus,
                filterSituacao,
                filterSacado,
                filterPesquisar,
                filterValor,
                filterCategoria,
                filterConta,
                filterDataEmissao,
                filterDataVencimento,
                filterDataQuitacao,
                filterMarcador,
                filterExcluidos,
                filterRenegociadas,
                selectAll,
                pagination,
                sortParams,
                filterPagForTipoPagamento,
                filterPagForStatus,
                filterPagForRemessa,
                filterPagForBancoDest,
            } = contasPagar;

            let newPagination = { ...pagination };

            // paginação precisa ser zerada antes de criar params
            if (
                type === GetTypes.recordFiltering ||
                type === GetTypes.addRecord ||
                type === GetTypes.singleRecord ||
                type === GetTypes.totalizadores
            ) {
                newPagination = {
                    current: 1,
                    pageSize: 10,
                    total: 0,
                };

                dispatch.contasPagar.setState({
                    pagination: newPagination,
                });
            }

            const params: BuildUrlParams = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_categoria_idpk: filterCategoria?.fca_idpk,
                financeiro_conta_idpk: filterConta?.fco_idpk,
                marcadores: marcadoresToStringIdpk(filterMarcador),
                qtde_registros: newPagination.pageSize || 10,
                registro_inicial:
                    ((newPagination.current ?? 0) - 1) * (newPagination.pageSize || 0),
                tipo_consulta,
                total_registros: type === GetTypes.paginationChange ? 'N' : 'S',
                ...(sortParams.shouldSort
                    ? { orderby: `${sortParams.fieldName} ${sortParams.orderDirection}` }
                    : { orderby: 'fmo_data_vencimento desc' }),
            };

            if (!filterStatus || filterStatus[0] === 'Total' || filterStatus.length === 0) {
                params.status = undefined;
            } else {
                params.status = filterStatus;
            }

            if (filterSacado?.fmo_fornecedor_idpk) {
                params.fornecedor_idpk = filterSacado.fmo_fornecedor_idpk;
            } else if (filterPesquisar) {
                params.pesquisar = filterPesquisar;
            }

            if (onlyPagFor) {
                params.where = params.where
                    ? ' AND fpa_pagfor_remessa_idpk is null'
                    : 'fpa_pagfor_remessa_idpk is null';
            }

            if (filterDataEmissao) {
                params.data_emissao_de = filterDataEmissao.start.format(DATE_FORMAT);
                params.data_emissao_ate = filterDataEmissao.end.format(DATE_FORMAT);
            }

            if (filterDataVencimento) {
                params.data_vencimento_de = filterDataVencimento.start.format(DATE_FORMAT);
                params.data_vencimento_ate = filterDataVencimento.end.format(DATE_FORMAT);
            }

            if (filterDataQuitacao) {
                params.data_quitacao_de = filterDataQuitacao.start.format(DATE_FORMAT);
                params.data_quitacao_ate = filterDataQuitacao.end.format(DATE_FORMAT);
            }

            if (filterValor) {
                params.valor_de = filterValor.start
                    ? filterValor.start.toString().trim().replace('.', ',')
                    : undefined;
                params.valor_ate = filterValor.end
                    ? filterValor.end.toString().trim().replace('.', ',')
                    : undefined;
            }

            if (filterSituacao && filterSituacao !== 0) {
                params.situacao = filterSituacao;
            }

            if (filterPagForTipoPagamento) {
                params.pagfor_tipo_pagamento = filterPagForTipoPagamento?.value;
            }

            if (filterPagForStatus) {
                params.pagfor_status = filterPagForStatus?.value;
            }

            if (filterPagForRemessa) {
                params.pagfor_remessa_idpk = filterPagForRemessa?.for_idpk;
            }

            if (filterPagForBancoDest) {
                params.pagfor_banco_destinatario_idpk = filterPagForBancoDest?.fba_idpk;
            }

            if (filterExcluidos) {
                params.excluidos = 'S';
            }

            if (filterRenegociadas) {
                params.renegociadas = 'S';
            }

            if (selectAll) {
                params.qtde_registros = undefined;
            }

            let url = '';

            if (type === GetTypes.customUrl && customUrl) {
                url = customUrl;
            } else if (type === GetTypes.singleRecord && conta_idpk) {
                url = buildUrl(Endpoint.FinanceiroMovimentoContaPagar, params, conta_idpk);
            } else {
                url = buildUrl(Endpoint.FinanceiroMovimentoContaPagar, params);
            }

            const response: ApiResponse = await comTokenGet(url);
            throwIfResponseIsErr(response);

            dispatch.contasPagar.setState({
                get: endReduxFnOk(response.data.registros || []),
            });

            // paginação e filtros devem ser zerados após um get que alteraria a qtde de paginas
            if (
                type === GetTypes.firstGet ||
                type === GetTypes.addRecord ||
                type === GetTypes.totalizadores
            ) {
                dispatch.contasPagar.setState({
                    pagination: {
                        current: 1,
                        pageSize: 10,
                        showSizeChanger: Number(response.data.total_registros) > 10,
                        showTotal: () => `Total de Registros: ${response.data.total_registros}`,
                        total: response.data.total_registros
                            ? Number(response.data.total_registros)
                            : 0,
                    },
                });
            }

            // a paginação que já existe no state deve ser mantida, mas o total/showTotal deve mudar
            if (type === GetTypes.deleteRecord || type === GetTypes.recordFiltering) {
                dispatch.contasPagar.setState({
                    pagination: {
                        ...pagination,
                        showSizeChanger: Number(response.data.total_registros) > 10,
                        showTotal: () => `Total de Registros: ${response.data.total_registros}`,
                        total: response.data.total_registros
                            ? Number(response.data.total_registros)
                            : 0,
                    },
                });
            }

            if (needTotais) {
                // todo get, exceto por paginacao, totalizadores e first-get com totalizador já cheio
                // (no caso de uma troca entre vendas-orçamentos-pedidos), deve gerar um novo totalizador
                if (
                    [
                        GetTypes.addRecord,
                        GetTypes.deleteRecord,
                        GetTypes.editRecord,
                        GetTypes.recordFiltering,
                    ].includes(type) ||
                    (type === GetTypes.firstGet &&
                        contasPagar.getTotais.data &&
                        contasPagar.getTotais.data.length === 0)
                ) {
                    await dispatch.contasPagar.getTotais(type);
                }
            }
        } catch (error) {
            dispatch.contasPagar.setState({
                get: endReduxFnError(error),
            });
        }
    },

    async getOne(
        payload: { conta_idpk?: number },
        state: RootState,
    ): Promise<ApiFinanceiroMovimento | null> {
        dispatch.contasPagar.setState({
            getOne: startReduxFn(state.contasPagar.getOne.data),
        });

        const { conta_idpk } = payload;
        const params = { empresa_idpk: getEmpresaIdpk() };

        const url = buildUrl(Endpoint.FinanceiroMovimentoContaPagar, params, conta_idpk);

        try {
            const response = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const { data: { registros = [] } = {} } = response;
            const conta = registros[0] || null;

            dispatch.contasPagar.setState({
                getOne: endReduxFnOk(conta),
            });

            return conta;
        } catch (error) {
            dispatch.contasPagar.setState({
                getOne: endReduxFnError(error),
            });

            return null;
        }
    },

    async getTotais(type: GetTypes, state: RootState): Promise<void> {
        dispatch.contasPagar.setState({
            getTotais: startReduxFn(state.contasPagar.getTotais.data),
        });

        try {
            const { contasPagar } = state;

            const {
                filterStatus,
                filterSituacao,
                filterPesquisar,
                filterSacado,
                filterValor,
                filterCategoria,
                filterDataEmissao,
                filterDataVencimento,
                filterDataQuitacao,
                filterMarcador,
                filterPagForTipoPagamento,
                filterPagForStatus,
                filterPagForRemessa,
                filterPagForBancoDest,
                filterExcluidos,
                filterRenegociadas,
                selectAll,
                pagination,
            } = contasPagar;

            let status: string[] | undefined;

            if (!filterStatus || filterStatus[0] === 'Total' || filterStatus.length === 0) {
                status = undefined;
            } else {
                status = filterStatus;
            }

            let newPagination = { ...pagination };

            // paginação precisa ser zerada antes de criar params
            if (
                type === GetTypes.recordFiltering ||
                type === GetTypes.addRecord ||
                type === GetTypes.singleRecord ||
                type === GetTypes.totalizadores
            ) {
                newPagination = {
                    current: 1,
                    pageSize: 10,
                    total: 0,
                };
            }

            const params: BuildUrlParams = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_categoria_idpk: filterCategoria ? filterCategoria.fca_idpk : undefined,
                marcadores: marcadoresToStringIdpk(filterMarcador),
                orderby: 'fmo_data_vencimento',
                qtde_registros: selectAll ? undefined : newPagination.pageSize || 10,
                registro_inicial:
                    ((newPagination.current ?? 0) - 1) * (newPagination.pageSize || 0),
                status,
                tipo: 'P',
            };

            if (filterSacado?.fmo_fornecedor_idpk) {
                params.fornecedor_idpk = filterSacado.fmo_fornecedor_idpk;
            } else if (filterPesquisar) {
                params.pesquisar = filterPesquisar;
            }

            if (filterDataEmissao) {
                params.data_emissao_de = filterDataEmissao.start.format(DATE_FORMAT);
                params.data_emissao_ate = filterDataEmissao.end.format(DATE_FORMAT);
            }

            if (filterDataVencimento) {
                params.data_vencimento_de = filterDataVencimento.start.format(DATE_FORMAT);
                params.data_vencimento_ate = filterDataVencimento.end.format(DATE_FORMAT);
            }

            if (filterDataQuitacao) {
                params.data_quitacao_de = filterDataQuitacao.start.format(DATE_FORMAT);
                params.data_quitacao_ate = filterDataQuitacao.end.format(DATE_FORMAT);
            }

            if (filterValor) {
                params.valor_de = filterValor.start
                    ? filterValor.start.toString().trim().replace('.', ',')
                    : undefined;
                params.valor_ate = filterValor.end
                    ? filterValor.end.toString().trim().replace('.', ',')
                    : undefined;
            }

            if (filterSituacao && filterSituacao !== 0) {
                params.situacao = filterSituacao;
            }

            if (filterPagForTipoPagamento) {
                params.pagfor_tipo_pagamento = filterPagForTipoPagamento?.value;
            }

            if (filterPagForStatus) {
                params.pagfor_status = filterPagForStatus?.value;
            }

            if (filterPagForRemessa) {
                params.pagfor_remessa_idpk = filterPagForRemessa?.for_idpk;
            }

            if (filterPagForBancoDest) {
                params.pagfor_banco_destinatario_idpk = filterPagForBancoDest?.fba_idpk;
            }

            if (filterExcluidos) {
                params.excluidos = 'S';
            }

            if (filterRenegociadas) {
                params.renegociadas = 'S';
            }

            const url = buildUrl(Endpoint.FinanceiroMovimentoValoresTotais, params);
            const response: ApiResponse = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const cores = {
                'A vencer': orange.primary,
                Pagos: green.primary,
                'Total do período': blue.primary,
                'Vencem hoje': red[7],
                Vencidos: red.primary,
            };

            const totais: TotalizadorListData[] = (response.data.registros || []).map(
                (total: Totalizador) => ({
                    color: cores[total.status],
                    quantity: total.quantidade_total,
                    title: total.status,
                    total: total.valor_total,
                    value: total.situacao?.toString() ?? '0',
                }),
            );

            dispatch.contasPagar.setState({
                getTotais: endReduxFnOk(totais),
            });
        } catch (error) {
            dispatch.contasPagar.setState({
                getTotais: endReduxFnError(error),
            });
        }
    },

    async post(
        payload: {
            agrupar: SimNao;
            body: ContasPagarPostBody;
            files: ContasPagarFile[];
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasPagar.setState({
            post: startReduxFn(state.contasPagar.post.data),
        });

        try {
            const { body, agrupar, files } = payload;
            const empresa_idpk = getEmpresaIdpk();

            const url = buildUrl(Endpoint.FinanceiroMovimentoContaPagar, {
                agrupar,
                empresa_idpk,
            });

            const response: ApiResponse = await comTokenPost(url, body);
            throwIfResponseIsErr(response);

            const financeiro_movimento_idpk: number = response.data.registros?.[0]
                ? response.data.registros[0].fmo_idpk
                : 0;

            await dispatch.contasPagar.postAnexo({
                files,
                financeiro_movimento_idpk,
            });

            notification.success({
                description: 'Conta cadastrada com sucesso',
                title: 'Feito!',
            });

            dispatch.contasPagar.setState({
                post: endReduxFnOk(financeiro_movimento_idpk),
                showModal: false,
            });

            void dispatch.contasPagar.get({
                type: GetTypes.addRecord,
            });
        } catch (error) {
            dispatch.contasPagar.setState({
                post: endReduxFnError(error),
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async postAnexo(
        payload: {
            financeiro_movimento_idpk: number;
            files: ContasPagarFile[];
        },
        state: RootState,
    ): Promise<void> {
        try {
            const { financeiro_movimento_idpk, files } = payload;

            if (files.length === 0) {
                return;
            }

            dispatch.contasPagar.setState({
                postAnexo: startReduxFn(state.contasPagar.postAnexo.data),
            });

            const params = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_movimento_idpk,
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoAnexo, params);
            const body = { registros: files };
            const response: ApiResponse = await comTokenPost(url, body);
            throwIfResponseIsErr(response);

            dispatch.contasPagar.setState({
                postAnexo: endReduxFnOk(null),
            });

            notification.success({
                description: 'Anexos foram cadastrados',
                title: 'Sucesso!',
            });
        } catch (error) {
            dispatch.contasPagar.setState({
                postAnexo: endReduxFnError(error),
            });

            notification.error({
                description: 'Falha ao cadastrar anexos',
                title: 'Falhou!',
            });
        }
    },

    async postLancamento(
        payload: {
            financeiro_movimento_idpk: number;
            body: ContasPagarPostLancamentoBody;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasPagar.setState({
            postLancamento: startReduxFn(state.contasPagar.postLancamento.data),
        });

        const { financeiro_movimento_idpk, body } = payload;

        try {
            const params = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_movimento_idpk,
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoLancamento, params);
            const response: ApiResponse = await comTokenPost(url, body);
            throwIfResponseIsErr(response);

            await dispatch.contasPagar.get({
                type: GetTypes.editRecord,
            });

            dispatch.contasPagar.setState({
                postLancamento: endReduxFnOk('success'),
            });
        } catch (error) {
            dispatch.contasPagar.setState({
                postLancamento: endReduxFnError(error),
            });
        }
    },

    async put(
        payload: {
            conta_idpk: number;
            body: ContasPagarPutBody;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasPagar.setState({
            put: startReduxFn(state.contasPagar.put.data),
        });

        try {
            const { conta_idpk, body } = payload;

            const params = {
                empresa_idpk: getEmpresaIdpk(),
                grupo_parcelas_alterar: 'U',
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoContaPagar, params, conta_idpk);
            const response: ApiResponse = await comTokenPut(url, body);
            throwIfResponseIsErr(response);

            notification.success({
                description: 'Conta atualizada com sucesso',
                title: 'Feito!',
            });

            void dispatch.contasPagar.get({
                type: GetTypes.editRecord,
            });

            dispatch.contasPagar.setState({
                put: endReduxFnOk('success'),
                showModal: false,
            });
        } catch (error) {
            dispatch.contasPagar.setState({
                put: endReduxFnError(error),
            });

            notification.error({
                description: 'Falha ao atualizar conta',
                title: 'Falhou!',
            });
        }
    },
});
